import {
	CheckCircleOutlined,
	CloudSyncOutlined,
	CloudUploadOutlined,
	UnorderedListOutlined
} from "@ant-design/icons";
import {
	Button,
	Card,
	Empty,
	Input,
	Space,
	Spin,
	Table,
	Tag,
	Tooltip
} from "antd";
import React, { useEffect, useState } from "react";
import {
	getProductLines,
	hasProductLines,
} from "../../services/productLinesService";
import { getAllProducts } from "../../services/productService";
import { syncProducts } from "../../services/productSyncService";
import { getSyncConfig } from "../../services/syncService";
import ProductLines from "./ProductLines";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { CustomFilterInput } from "../Textfield/CustomFilterInput";

const ProductList = () => {
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [syncingAll, setSyncingAll] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const isMobile = window.innerWidth < 768;
	const [config, setConfig] = useState({
		interval: 30,
		intervalUnit: "segundos",
	});
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [viewingLines, setViewingLines] = useState(false);
	const [productsWithLines, setProductsWithLines] = useState({});
	const [productsLineCount, setProductsLineCount] = useState({});

	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
	});

	const { country } = useConfig();

	// Cargar la configuración al inicio
	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const syncConfig = await getSyncConfig();
				setConfig(syncConfig);
			} catch (error) {
				console.error("Error al cargar configuración:", error);
			}
		};

		fetchConfig();
	}, []);

	useEffect(() => {
		loadProducts();
	}, []);

	// Actualizar la lista según el intervalo configurado
	useEffect(() => {
		const interval = setInterval(() => {
			loadProducts();
		}, config.interval * (config.intervalUnit === "segundos" ? 1000 : config.intervalUnit === "minutos" ? 60000 : 3600000)); // Convertir a milisegundos

		// Limpiar intervalo al desmontar
		return () => clearInterval(interval);
	}, [config]);

	// Comprobar qué productos tienen líneas asociadas
	useEffect(() => {
		const checkProductLines = async () => {
			const productsWithLinesMap = {};
			const productsLineCountMap = {};

			for (const product of products) {
				if (product.No) {
					try {
						const hasLines = await hasProductLines(product.No);
						productsWithLinesMap[product.id] = hasLines;

						if (hasLines) {
							// Si tiene líneas, obtenemos la cantidad
							const lines = await getProductLines(product.No);
							productsLineCountMap[product.id] = lines.length;
						}
					} catch (error) {
						console.error(
							`Error al verificar líneas para producto ${product.No}:`,
							error
						);
					}
				}
			}

			setProductsWithLines(productsWithLinesMap);
			setProductsLineCount(productsLineCountMap);
		};

		if (products.length > 0) {
			checkProductLines();
		}
	}, [products]);

	const loadProducts = async () => {
		try {
			console.log("Cargando productos...");
			setLoading(true);
			const productList = await getAllProducts();
			setProducts(productList);
		} catch (error) {
			window.messageApi.error("Error al cargar productos: " + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleViewLines = (product) => {
		setSelectedProduct(product);
		setViewingLines(true);
	};

	const handleBackFromLines = () => {
		setViewingLines(false);
		setSelectedProduct(null);
	};

	// Renderizar el estado de sincronización
	const renderSyncStatus = (pendingSync, lastSynced) => {
		if (pendingSync === 1) {
			return (
				<Tooltip title="Pendiente de sincronizar">
					<Tag icon={<CloudUploadOutlined />} color="warning">
						Pendiente
					</Tag>
				</Tooltip>
			);
		} else {
			const syncDate = lastSynced
				? new Date(lastSynced).toLocaleString()
				: "Nunca";

			return (
				<Tooltip title={`Última sincronización: ${syncDate}`}>
					<Tag icon={<CheckCircleOutlined />} color="success">
						Sincronizado
					</Tag>
				</Tooltip>
			);
		}
	};

	// Sincronizar productos de esta pantalla
	const handleSyncAll = async () => {
		try {
			setSyncingAll(true);
			await syncProducts();
			// Recargar la lista para reflejar los cambios
			await loadProducts();
			window.messageApi.success("Productos sincronizados correctamente");
		} catch (error) {
			window.messageApi.error(
				"Error sincronizando productos: " + error.message
			);
		} finally {
			setSyncingAll(false);
		}
	};

	// Función para filtrar productos según el término de búsqueda
	const filterProducts = (products, searchTerm) => {
		if (!searchTerm) return products;

		const lowercaseSearchTerm = searchTerm.toLowerCase();

		return products.filter(product => {
			return (
				(product.id && product.id.toString().toLowerCase().includes(lowercaseSearchTerm)) ||
				(product.No && product.No.toLowerCase().includes(lowercaseSearchTerm)) ||
				(product.barcode && product.barcode.toLowerCase().includes(lowercaseSearchTerm)) ||
				(product.description && product.description.toLowerCase().includes(lowercaseSearchTerm)) ||
				(product.Unit_Price && product.Unit_Price.toString().includes(lowercaseSearchTerm)) ||
				(product.PorIva && product.PorIva.toString().includes(lowercaseSearchTerm))
			);
		});
	};

	// Aplicar filtro a los productos
	const filteredProducts = filterProducts(products, searchTerm);

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
		},
		{
			title: "No.",
			dataIndex: "No",
			key: "No",
			filterDropdown: (props) => <CustomFilterInput {...props} />,
			onFilter: (value, record) =>
				record.No?.toLowerCase().includes(value.toLowerCase()),
			render: (No) => No || "Pendiente",
		},
		{
			title: "Código de Barras",
			dataIndex: "barcode",
			key: "barcode",
			filterDropdown: (props) => <CustomFilterInput {...props} />,
			onFilter: (value, record) => {
				const barcode = record.barcode ?? "";
				return barcode.toString().toLowerCase().includes(value.toLowerCase());
			},
			sorter: (a, b) => {
				const ba = a.barcode ?? ""; 
				const bb = b.barcode ?? "";
				return ba.toString().localeCompare(bb.toString());
			},
			render: (barcode) => barcode || "-",
		},
		{
			title: "Descripción",
			dataIndex: "description",
			key: "description",
			filterDropdown: (props) => <CustomFilterInput {...props} />,
			onFilter: (value, record) =>
				record.description?.toLowerCase().includes(value.toLowerCase()),
		},
		{
			title: "Precio Unitario",
			dataIndex: "Unit_Price",
			key: "Unit_Price",
			render: (price) => formatNumberByCountry(price || 0, country, true),
			sorter: (a, b) => a.Unit_Price - b.Unit_Price,
		},
		{
			title: "IVA (%)",
			dataIndex: "PorIva",
			key: "PorIva",
			render: (iva) => `${iva ? iva.toFixed(2) : "0.00"}%`,
			sorter: (a, b) => (a.PorIva || 0) - (b.PorIva || 0),
		},
		{
			title: "Estado",
			key: "syncStatus",
			render: (_, record) =>
				renderSyncStatus(record.pendingSync, record.lastSynced),
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => (
				<Space size="middle">
					{record.No && (
						<Button
							type="primary"
							ghost
							icon={<UnorderedListOutlined />}
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								handleViewLines(record);
							}}
							style={{ display: "flex", alignItems: "center" }}
						>
							<span>Kit</span>
							<Tag
								color={productsWithLines[record.id] ? "blue" : "default"}
								style={{
									marginLeft: 5,
									marginRight: 0,
									borderRadius: "50%",
									minWidth: "22px",
									height: "22px",
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									fontWeight: "bold",
								}}
							>
								{productsLineCount[record.id] || 0}
							</Tag>
						</Button>
					)}
				</Space >
			),
		},
	];

	if (loading) {
		return <Spin size="large" />;
	}

	if (viewingLines && selectedProduct) {
		return (
			<ProductLines product={selectedProduct} onBack={handleBackFromLines} />
		);
	}

	if (filteredProducts.length === 0 && searchTerm) {
		return (
			<div className="product-list-container">
				<Card
					title={isMobile ? "Productos" : "Productos Registrados"}
					extra={
						<Space>
							<Input.Search
								placeholder="Buscar productos..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onSearch={(value) => setSearchTerm(value)}
								style={{ width: isMobile ? 150 : 200 }}
								allowClear
							/>
							<Button
								icon={<CloudSyncOutlined />}
								onClick={handleSyncAll}
								loading={syncingAll}
								type="primary"
							>
								{isMobile ? "" : "Sincronizar productos"}
							</Button>
						</Space>
					}
				>
					<Empty description={`No se encontraron productos que coincidan con "${searchTerm}"`} />
				</Card>
			</div>
		);
	}

	if (products.length === 0) {
		return <Empty description="No hay productos registrados aún" />;
	}

	return (
		<div className="product-list-container">
			<Card
				title={isMobile ? "Productos" : "Productos Registrados"}
				extra={
					<Space>
						<Input.Search
							placeholder="Buscar productos..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onSearch={(value) => setSearchTerm(value)}
							style={{ width: isMobile ? 150 : 200 }}
							allowClear
						/>
						<Button
							icon={<CloudSyncOutlined />}
							onClick={handleSyncAll}
							loading={syncingAll}
							type="primary"
						>
							{isMobile ? "" : "Sincronizar productos"}
						</Button>
					</Space>
				}
			>
				<Table
					columns={columns}
					dataSource={filteredProducts.map((product) => ({
						...product,
						key: product.id,
					}))}
					pagination={{
						...pagination,
						showSizeChanger: true,
						showQuickJumper: true,
						pageSizeOptions: ["5", "10", "20", "50"],
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} de ${total} elementos`,
					}}
					onChange={(newPagination) => setPagination(newPagination)}
					bordered
					size="middle"
					scroll={{ x: "max-content" }}
					onRow={(record) => ({
						onClick: () => {
							if (record.No) {
								handleViewLines(record);
							}
						},
						style: {
							cursor: record.No ? "pointer" : "default",
							background: record.No ? "rgba(24, 144, 255, 0.05)" : "inherit",
						},
					})}
				/>
			</Card>
		</div>
	);
};

export default ProductList;
